import { useState } from "react";
import { api, apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SPECIES = ["Perro", "Gato"];
const STUDIES = ["Ecografía abdominal", "Ecocardiografía", "Ecografía de gestación", "Intervencionismo", "Otra"];

export function EditStudyDialog({ b, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patient_name: b.patient_name || "", owner_name: b.owner_name || "",
    species: b.species || "", age: b.age || "", weight: b.weight || "",
    study_type: b.study_type || "", reason: b.reason || "", notes: b.notes || "",
  });

  const open_ = (o) => {
    setOpen(o);
    if (o) setForm({
      patient_name: b.patient_name || "", owner_name: b.owner_name || "",
      species: b.species || "", age: b.age || "", weight: b.weight || "",
      study_type: b.study_type || "", reason: b.reason || "", notes: b.notes || "",
    });
  };

  const save = async () => {
    if (!form.patient_name) { toast.error("El nombre del paciente es obligatorio"); return; }
    setSaving(true);
    try {
      await api.patch(`/bookings/${b.id}/details`, form);
      toast.success("Datos del estudio actualizados");
      setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const speciesOptions = SPECIES.includes(form.species) || !form.species ? SPECIES : [...SPECIES, form.species];
  const studyOptions = STUDIES.includes(form.study_type) || !form.study_type ? STUDIES : [...STUDIES, form.study_type];

  return (
    <Dialog open={open} onOpenChange={open_}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-xl border-slate-300 font-semibold text-[#373E45]" data-testid={`edit-study-${b.id}`}>
          <Pencil className="h-4 w-4 mr-1" />Editar datos
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`edit-study-dialog-${b.id}`}>
        <DialogHeader>
          <DialogTitle>Editar datos del estudio{b.case_number ? ` · ${b.case_number}` : ""}</DialogTitle>
          <DialogDescription>Corrige o completa los datos del paciente y del estudio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#373E45] font-semibold">Nombre del paciente</Label>
              <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1.5 bg-slate-50" data-testid={`edit-patient-${b.id}`} />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Propietario</Label>
              <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="Ej. María García" data-testid={`edit-owner-${b.id}`} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[#373E45] font-semibold">Especie</Label>
              <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-50" data-testid={`edit-species-${b.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{speciesOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Edad</Label>
              <Input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 bg-slate-50" data-testid={`edit-age-${b.id}`} />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Peso</Label>
              <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="mt-1.5 bg-slate-50" data-testid={`edit-weight-${b.id}`} />
            </div>
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Tipo de estudio</Label>
            <Select value={form.study_type} onValueChange={(v) => setForm({ ...form, study_type: v })}>
              <SelectTrigger className="mt-1.5 bg-slate-50" data-testid={`edit-study-type-${b.id}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{studyOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Motivo / sospecha clínica</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 bg-slate-50" data-testid={`edit-reason-${b.id}`} />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Comentarios</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5 bg-slate-50 resize-none" data-testid={`edit-notes-${b.id}`} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid={`edit-save-${b.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar cambios"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
